import classNames from "classnames";
import { useContext, useEffect, useRef, useState } from "react";

import Datepicker from "src/app/components/datepicker/Datepicker";
import { Icon } from "src/app/components/icon/Icon";
import MultiFilterDropdown from "src/app/components/multiFilterDropdown/MultiFilterDropdown";
import adminCSS from "src/app/modules/admin/Admin.module.css";
import { TARGET_GROUPS } from "src/app/modules/auth/constants";
import { BASE_SYSTEMS } from "src/app/modules/home/constants";
import projectCSS from "src/app/modules/user-projects/Projects.module.css";
import { ModalContext } from "src/contexts/ModalContext";
import { ApiResult } from "src/core/helpers/api/ApiResult";
import { CityResponse } from "src/core/models/partners/CityResponse";
import { PartnerResponse } from "src/core/models/partners/PartnerResponse";
import { RegionResponse } from "src/core/models/partners/RegionResponse";
import { PartnersService } from "src/core/services/partners";

const DataFiltering = ({
  filteredPartnerList,
  filteredRegionList,
  filteredCityList,
  filteredSystemList,
  filteredTargetGroupList,
  filteredDateFrom,
  filteredDateTo,
  setFilteredPartnerList,
  setFilteredRegionList,
  setFilteredCityList,
  setFilteredSystemList,
  setFilteredTargetGroupList,
  setFilteredDateFrom,
  setFilteredDateTo,
}: {
  filteredPartnerList: Array<number> | null;
  filteredRegionList: Array<number> | null;
  filteredCityList: Array<number> | null;
  filteredSystemList: Array<number> | null;
  filteredTargetGroupList: Array<number> | null;
  filteredDateFrom: Date | undefined;
  filteredDateTo: Date | undefined;
  setFilteredPartnerList: (value: number[]) => void;
  setFilteredRegionList: (value: number[]) => void;
  setFilteredCityList: (value: number[]) => void;
  setFilteredSystemList: (value: number[]) => void;
  setFilteredTargetGroupList: (value: number[]) => void;
  setFilteredDateFrom: (value: Date | undefined) => void;
  setFilteredDateTo: (value: Date | undefined) => void;
}) => {
  const [showFiltering, setShowFiltering] = useState(false);
  const [partnersSelectList, setPartnersSelectList] = useState<Array<any>>([]);
  const [regionsSelectList, setRegionsSelectList] = useState<Array<any>>([]);
  const [citiesSelectList, setCitiesSelectList] = useState<Array<any>>([]);

  const buttonContainer = useRef<any>(null);
  const container = useRef<any>(null);
  const modalData = useContext(ModalContext);

  const targetGroupsSelectList = TARGET_GROUPS.combineValues();
  const systemsSelectList = BASE_SYSTEMS.map((x: any) => {
    const value = x.baseSystemId;
    const text = x.name;
    return { value: value, text: text };
  });

  useEffect(() => {
    PartnersService.getCities()
      .then((response) => {
        if (response) {
          setCitiesSelectList(
            response.map((city: CityResponse) => {
              let value = city.id;
              let text = city.name;
              let region = city.regionId;
              return { value: value, text: text, region: region };
            })
          );
        }
      })
      .catch((e: ApiResult) => {
        if (!modalData?.content?.contentSet) {
          modalData?.triggerError(e);
        }
      });

    PartnersService.getRegions()
      .then((response) => {
        if (response) {
          setRegionsSelectList(
            response.map((region: RegionResponse) => {
              let value = region.id;
              let text = region.name;
              return { value: value, text: text };
            })
          );
        }
      })
      .catch((e: ApiResult) => {
        if (!modalData?.content?.contentSet) {
          modalData?.triggerError(e);
        }
      });

    PartnersService.getAll()
      .then((response) => {
        if (response) {
          const filteredPartners = response
            .filter((partner: PartnerResponse) => {
              return partner.locationX && partner.locationY;
            })
            .sort((partnerA: PartnerResponse, partnerB: PartnerResponse) => (partnerA!.name!.toLowerCase() > partnerB!.name!.toLowerCase() ? 1 : -1));
          setPartnersSelectList(
            filteredPartners.map((partner: PartnerResponse) => {
              let value = partner.id;
              let text = `${partner.name}, ${partner.zipCode} - ${partner.address}`;
              let region = partner.regionId;
              let city = partner.cityId;
              return { value: value, text: text, region: region, city: city };
            })
          );
        }
      })
      .catch((e: ApiResult) => {
        if (!modalData?.content?.contentSet) {
          modalData?.triggerError(e);
        }
      });
  }, [modalData]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  });

  const handleClickOutside = (event: MouseEvent) => {
    if (container.current && !container.current.contains(event.target) && buttonContainer.current && !buttonContainer.current.contains(event.target)) {
      setShowFiltering(false);
    }
  };

  const onRegionChange = (regionId: number) => {
    const newFilters = getFilteredList(filteredRegionList, regionId);
    setFilteredRegionList(newFilters);

    const filteredSelectCities = citiesSelectList.filter((item) => (newFilters && newFilters.length > 0 ? newFilters.includes(item.region) : true));
    let filteredSelectPartners = partnersSelectList.filter((item) => (newFilters && newFilters.length > 0 ? newFilters.includes(item.region) : true));

    const newCityFilters = filteredCityList!.filter((item) => filteredSelectCities.some((x) => x.value === item));
    setFilteredCityList(newCityFilters);

    filteredSelectPartners = filteredSelectPartners.filter((item) => (newCityFilters && newCityFilters.length > 0 ? newCityFilters.includes(item.city) : true));
    const newPartnerFilters = filteredPartnerList!.filter((item) => filteredSelectPartners.some((x) => x.value === item));
    setFilteredPartnerList(newPartnerFilters);
  };

  const onCityChange = (cityId: number) => {
    const newFilters = getFilteredList(filteredCityList, cityId);
    setFilteredCityList(newFilters);

    let filteredSelectPartners = partnersSelectList.filter((item) =>
      filteredRegionList && filteredRegionList.length > 0 ? filteredRegionList.includes(item.region) : true
    );
    filteredSelectPartners = filteredSelectPartners.filter((item) => (newFilters && newFilters.length > 0 ? newFilters.includes(item.city) : true));
    const newPartnerFilters = filteredPartnerList!.filter((item) => filteredSelectPartners.some((x) => x.value === item));
    setFilteredPartnerList(newPartnerFilters);
  };

  const onPartnerChange = (partnerId: number) => {
    const newFilters = getFilteredList(filteredPartnerList, partnerId);
    setFilteredPartnerList(newFilters);
  };

  const onSystemChange = (systemId: number) => {
    const newFilters = getFilteredList(filteredSystemList, systemId);
    setFilteredSystemList(newFilters);
  };

  const onTargetGroupChange = (targetGroupId: number) => {
    const newFilters = getFilteredList(filteredTargetGroupList, targetGroupId);
    setFilteredTargetGroupList(newFilters);
  };

  const onDateChange = (dates: any) => {
    const [start, end] = dates;
    setFilteredDateFrom(start);
    setFilteredDateTo(end);
  };

  const getFilteredList = (appliedFilters: Array<number> | null, value: number) => {
    let newFilters = appliedFilters ? [...appliedFilters] : [];
    if (!newFilters?.includes(value)) {
      newFilters?.push(value);
    } else {
      newFilters.splice(newFilters.indexOf(value), 1);
    }
    return newFilters;
  };

  const removeAllFilters = () => {
    setFilteredPartnerList([]);
    setFilteredRegionList([]);
    setFilteredCityList([]);
    setFilteredDateFrom(undefined);
    setFilteredDateTo(undefined);
  };

  return (
    <>
      <div className="u-flex u-flex-gap-5 u-flex-wrap u-pr10px">
        <button
          onClick={() => setShowFiltering(!showFiltering)}
          className={classNames(
            showFiltering ? adminCSS.colorChange : "u-bg-primary-dark",
            "u-flex",
            "u-flex-items-base",
            "u-hfit",
            "u-cursor-pointer",
            adminCSS.filterButton
          )}
          ref={buttonContainer}
        >
          <Icon name="filter" className="u-mr5px" />
          <h6 className="u-m0 u-t-white u-t-w400">Filtriraj</h6>
        </button>
        {filteredPartnerList &&
          filteredPartnerList.map((item) => (
            <div className={classNames("u-bg-white", "u-flex", "u-flex-items-center", "u-hfit", adminCSS.filterApplied)}>
              <h6 className="u-m0 u-t-black u-t-w400 ">{partnersSelectList.find((partner) => partner.value === item)?.text}</h6>
              <button onClick={() => onPartnerChange(item)} className="u-pt5px u-p0 u-cursor-pointer">
                <Icon name="remove-filter" className="u-ml5px u-mt5px" />
              </button>
            </div>
          ))}
        {filteredRegionList &&
          filteredRegionList.map((item) => (
            <div className={classNames("u-bg-white", "u-flex", "u-flex-items-center", "u-hfit", adminCSS.filterApplied)}>
              <h6 className="u-m0 u-t-black u-t-w400">{regionsSelectList.find((region) => region.value === item)?.text}</h6>
              <button onClick={() => onRegionChange(item)} className="u-pt5px u-p0 u-cursor-pointer">
                <Icon name="remove-filter" className="u-ml5px u-mt5px" />
              </button>
            </div>
          ))}
        {filteredCityList &&
          filteredCityList.map((item) => (
            <div className={classNames("u-bg-white", "u-flex", "u-flex-items-center", "u-hfit", adminCSS.filterApplied)}>
              <h6 className="u-m0 u-t-black u-t-w400">{citiesSelectList.find((city) => city.value === item)?.text}</h6>
              <button onClick={() => onCityChange(item)} className="u-pt5px u-p0 u-cursor-pointer">
                <Icon name="remove-filter" className="u-ml5px u-mt5px" />
              </button>
            </div>
          ))}
        {filteredSystemList &&
          filteredSystemList.map((item) => (
            <div className={classNames("u-bg-white", "u-flex", "u-flex-items-center", "u-hfit", adminCSS.filterApplied)}>
              <h6 className="u-m0 u-t-black u-t-w400">{systemsSelectList.find((system) => system.value === item)?.text}</h6>
              <button onClick={() => onSystemChange(item)} className="u-pt5px u-p0 u-cursor-pointer">
                <Icon name="remove-filter" className="u-ml5px u-mt5px" />
              </button>
            </div>
          ))}
        {filteredTargetGroupList &&
          filteredTargetGroupList.map((item) => (
            <div className={classNames("u-bg-white", "u-flex", "u-flex-items-center", "u-hfit", adminCSS.filterApplied)}>
              <h6 className="u-m0 u-t-black u-t-w400">{targetGroupsSelectList.find((targetGroup: any) => targetGroup.value === item)?.text}</h6>
              <button onClick={() => onTargetGroupChange(item)} className="u-pt5px u-p0 u-cursor-pointer">
                <Icon name="remove-filter" className="u-ml5px u-mt5px" />
              </button>
            </div>
          ))}
      </div>
      {showFiltering && (
        <div className={classNames("u-bg-background", "u-flex", "u-flex-column", adminCSS.filteringModal)} ref={container}>
          <h5 className="u-m0 u-w-700 u-t-primary u-mb20px">Filtriraj partnere</h5>
          <div className={classNames("u-flex", "u-flex-column", "u-w95", "u-flex-gap-10", "u-t-align-start", projectCSS.inputs)}>
            <div>
              <label className="u-t-f14p u-ml10px u-t-w400 u-t-lh20" htmlFor="partner">
                Regija
              </label>
              <MultiFilterDropdown
                items={regionsSelectList}
                activeItemValues={filteredRegionList}
                defaultItem={"sve regije"}
                handleChange={(regionId: number) => {
                  onRegionChange(regionId);
                }}
                placeholder={"Pronađi regiju"}
                customCss={""}
              />
            </div>
            <div>
              <label className="u-t-f14p u-ml10px u-t-w400 u-t-lh20" htmlFor="partner">
                Grad
              </label>
              <MultiFilterDropdown
                items={citiesSelectList.filter((item) =>
                  filteredRegionList && filteredRegionList.length > 0 ? filteredRegionList.includes(item.region) : true
                )}
                activeItemValues={filteredCityList}
                defaultItem={"svi gradovi"}
                handleChange={(cityId: number) => {
                  onCityChange(cityId);
                }}
                placeholder={"Pronađi grad"}
                customCss={""}
              />
            </div>
            <div>
              <label className="u-t-f14p u-ml10px u-t-w400 u-t-lh20 u-t0black" htmlFor="partner">
                Pronadi partnera
              </label>
              <MultiFilterDropdown
                items={partnersSelectList
                  .filter((item) => (filteredRegionList && filteredRegionList.length > 0 ? filteredRegionList.includes(item.region) : true))
                  .filter((item) => (filteredCityList && filteredCityList.length > 0 ? filteredCityList.includes(item.city) : true))}
                activeItemValues={filteredPartnerList}
                defaultItem={"svi partneri"}
                handleChange={(partnerId: number) => {
                  onPartnerChange(partnerId);
                }}
                placeholder={"Pronađi partnera"}
                customCss={""}
              />
            </div>
            <div>
              <label className="u-t-f14p u-ml10px u-t-w400 u-t-lh20 u-t0black" htmlFor="system">
                Pronadi sustav
              </label>
              <MultiFilterDropdown
                items={systemsSelectList}
                activeItemValues={filteredSystemList}
                defaultItem={"svi sustavi"}
                handleChange={(systemId: number) => {
                  onSystemChange(systemId);
                }}
                placeholder={"Pronađi sustav"}
                customCss={""}
              />
            </div>
            <div>
              <label className="u-t-f14p u-ml10px u-t-w400 u-t-lh20 u-t0black" htmlFor="targetGroup">
                Pronadi ciljnu skupinu
              </label>
              <MultiFilterDropdown
                items={targetGroupsSelectList}
                activeItemValues={filteredTargetGroupList}
                defaultItem={"sve ciljne skupine"}
                handleChange={(targetGroupId: number) => {
                  onTargetGroupChange(targetGroupId);
                }}
                placeholder={"Pronađi ciljnu skupinu"}
                customCss={""}
              />
            </div>
            <div className="partner-statistic-date-filter u-mb15px">
              <label className="u-t-f14p u-ml10px u-t-w400 u-t-lh20" htmlFor="partner">
                Filtriraj po vremenu
              </label>
              <Datepicker startDate={filteredDateFrom} endDate={filteredDateTo} handleChange={onDateChange} />
            </div>
          </div>
          <button
            onClick={() => removeAllFilters()}
            className={classNames("u-flex", "u-flex-items-center", "u-hfit", "u-flex-gap-5", "u-cursor-pointer", adminCSS.removeAllFilters)}
          >
            <h6 className="u-m0 u-t-primary u-t-w400">ukloni sve filtere</h6>
            <Icon name="trash" className="u-t-primary" />
          </button>
        </div>
      )}
    </>
  );
};

export default DataFiltering;
